/**
 * Widget-snapshot: kompakt, ferdig-beregnet tilstand som web/TS-laget skriver til
 * delt lagring (iOS App Group / Android SharedPreferences) via `WidgetBridge`.
 * Home screen-widgeten er en DUM RENDERER som kun leser dette snapshotet — all
 * logikk blir her, i testet TS.
 *
 * PR 1 (bro + Widget 1 «Nærmeste fricamp») trenger kun posisjon + nærmeste plass.
 * `fireDanger`/`lastVisit` legges til additivt når Widget 2/3 lander.
 *
 *   spots + GPS  ──►  buildWidgetSnapshot()  ──►  WidgetSnapshot (JSON)
 *                                                      │
 *                          shouldWriteSnapshot() gate ─┘ (unngå write-amplification)
 */
import type { CampingSpot, Coordinates } from '../types';
import { getDistance } from './geometryUtils';

/** Språk-agnostisk egnethets-nivå. Native renderer lokaliserer til etikett. */
export type SuitabilityTier = 'good' | 'medium' | 'poor';

export interface WidgetNearestSpot {
  id: string;
  name: string;
  distanceMeters: number;
  /** Rå spotType — native/renderer mapper til visningsetikett. */
  spotType: string;
  /** 0–10 egnethets-score. */
  score: number;
  /** Hex — foretrekker plassens egen suitabilityColor, ellers utledet fra score. */
  suitabilityColor: string;
  /** Fargeblind-trygt tekst-signal (a11y): renderer viser etikett, ikke bare farge. */
  suitabilityTier: SuitabilityTier;
  isDriveable: boolean;
}

export interface WidgetSnapshot {
  updatedAt: number;
  hasPremium: boolean;
  lang: string;
  position: { lat: number; lon: number } | null;
  nearestSpot: WidgetNearestSpot | null;
}

export interface BuildSnapshotInput {
  position: Coordinates | null | undefined;
  spots: CampingSpot[];
  hasPremium: boolean;
  lang: string;
  /** Injiserbar «nå» for deterministiske tester. */
  now: number;
}

/**
 * Egnethets-nivå + fallback-farge fra score. Samme terskler som den native
 * `CarSpotData` (score >= 8 grønn, >= 4 gul, ellers rød) — DRY med bil-visningen.
 */
export function suitabilityTier(score: number): { color: string; tier: SuitabilityTier } {
  if (score >= 8) return { color: '#22c55e', tier: 'good' };
  if (score >= 4) return { color: '#eab308', tier: 'medium' };
  return { color: '#ef4444', tier: 'poor' };
}

/** Kan denne plassen vises som «nærmeste fricamp»? Ekskluderer ugyldige/forbudte. */
function isSurfaceable(s: CampingSpot): boolean {
  if (!s.location) return false;
  if (s.isDeleted || s.isInvalid) return false;
  if (s.spotType === 'forbidden_area') return false;
  return true;
}

function toNearestSpot(spot: CampingSpot, distanceMeters: number): WidgetNearestSpot {
  const score = spot.aiResult?.score ?? spot.score ?? 5;
  const { color, tier } = suitabilityTier(score);
  return {
    id: spot.id,
    name: spot.name || '',
    distanceMeters: Math.round(distanceMeters),
    spotType: spot.spotType,
    score,
    // Plassens egen farge vinner (satt av admin/AI); ellers score-utledet fallback.
    suitabilityColor: spot.suitabilityColor ?? color,
    suitabilityTier: tier,
    // Samme regel som Android Auto: kystplasser/severdigheter er ikke kjørbare mål.
    isDriveable: spot.spotType !== 'coastal' && spot.spotType !== 'attraction',
  };
}

/** Nærmeste egnede plass fra GPS-posisjon, eller null. Ren/testbar. */
export function findNearestSpot(
  position: Coordinates,
  spots: CampingSpot[],
): WidgetNearestSpot | null {
  let best: CampingSpot | null = null;
  let bestDist = Infinity;
  for (const s of spots) {
    if (!isSurfaceable(s)) continue;
    const d = getDistance(position.latitude, position.longitude, s.location.latitude, s.location.longitude);
    if (d < bestDist) {
      bestDist = d;
      best = s;
    }
  }
  return best ? toNearestSpot(best, bestDist) : null;
}

/**
 * Bygg widget-snapshotet. Uten posisjon: `position` og `nearestSpot` = null
 * (widgeten viser tom-tilstand). `hasPremium=false` bygger snapshotet LIKEVEL —
 * Widget 1 er en gratis trakt.
 */
export function buildWidgetSnapshot(input: BuildSnapshotInput): WidgetSnapshot {
  const { position, spots, hasPremium, lang, now } = input;
  return {
    updatedAt: now,
    hasPremium,
    lang,
    position: position ? { lat: position.latitude, lon: position.longitude } : null,
    nearestSpot: position ? findNearestSpot(position, spots) : null,
  };
}

/**
 * Skal vi skrive et nytt snapshot? Demper write-amplification: skriv kun når noe
 * synlig endrer seg — nærmeste plass, premium, språk, posisjon-tilstedeværelse,
 * eller flytting > terskel. Hindrer disk-skriv + timeline-reload på hvert GPS-punkt.
 */
export function shouldWriteSnapshot(
  prev: WidgetSnapshot | null,
  next: WidgetSnapshot,
  moveThresholdMeters = 200,
): boolean {
  if (!prev) return true;
  if ((prev.nearestSpot?.id ?? null) !== (next.nearestSpot?.id ?? null)) return true;
  if (prev.hasPremium !== next.hasPremium) return true;
  if (prev.lang !== next.lang) return true;

  const prevHasPos = prev.position !== null;
  const nextHasPos = next.position !== null;
  if (prevHasPos !== nextHasPos) return true;

  if (prev.position && next.position) {
    const moved = getDistance(prev.position.lat, prev.position.lon, next.position.lat, next.position.lon);
    return moved > moveThresholdMeters;
  }
  return false;
}
